import { useScreen } from "@hooks/useScreen";
import { createContext, useCallback, useContext, useState } from "react";

export interface PlayingState {
  playlistId: number | undefined,
  state: "playing" | "paused" | "loading" | "screenshot_loading",
}

export type GlobalMusicReturnProps = {
  playingStates: Record<string, PlayingState>;
  updatePlayingState: (screenId: string, state: Partial<PlayingState>) => void;
  updatePlayingStates: (states: Record<string, PlayingState>) => void;
  updateGroupPlayingState: (groupId: number, state: Partial<PlayingState>) => void;
};

export const globalMusicContext = createContext<GlobalMusicReturnProps | null>(null);

const { Provider } = globalMusicContext;

export function GlobalMusicProvider({ children }: React.PropsWithChildren<{}>) {
  const [playingStates, setPlayingStates] = useState<Record<string, PlayingState>>({});

  const updatePlayingState = useCallback((screenId: string, state: Partial<PlayingState>) => {
    setPlayingStates(prev => ({
      ...prev,
      [screenId]: {
        ...prev[screenId],
        ...state,
      },
    }));
  }, []);

  const updateGroupPlayingState = useCallback((groupId: number, state: Partial<PlayingState>) => {
    setPlayingStates(prev => {
      const newStates = { ...prev };
      for (const screenId in prev) {
        if (prev[screenId].playlistId === groupId) {
          newStates[screenId] = {
            ...prev[screenId],
            ...state,
          };
        }
      }
      return newStates;
    })
  }, [])

  const updatePlayingStates = useCallback((states: Record<string, PlayingState>) => {
    setPlayingStates(prev => ({
      ...prev,
      ...states,
    }));
  }, [])

  return (
    <Provider
      value={{
        playingStates,
        updatePlayingState,
        updateGroupPlayingState,
        updatePlayingStates,
      }}
    >
      {children}
    </Provider>
  );
}

export function useGlobalMusic(): GlobalMusicReturnProps {
  const context = useContext(globalMusicContext);
  if (!context) {
    throw new Error("useGlobalMusic must be used within a GlobalMusicProvider");
  }
  return context;
}

export interface LocalMusicReturnProps extends PlayingState {
}

export const LocalMusicContext = createContext<LocalMusicReturnProps | null>(null);

export function LocalMusicProvider({ children }: React.PropsWithChildren<{}>) {
  const { playingStates } = useGlobalMusic();
  const screen = useScreen();

  if (!screen?.screenInfo?.id) throw "useLocalMusic must be used within a ScreenProvider";
  const { screenInfo } = screen;

  const state = playingStates[screenInfo.id!] ?? { playlistId: undefined, state: "paused" };

  return (
    <LocalMusicContext.Provider
      value={state}
    >
      {children}
    </LocalMusicContext.Provider>
  );
}

export function useLocalMusic(): LocalMusicReturnProps {
  const context = useContext(LocalMusicContext);
  if (!context) {
    throw new Error("useLocalMusic must be used within a LocalMusicProvider");
  }
  return context;
}
